import { sessionStorageService } from "../../../../auth/session";
const apiBase = (process.env.ITR_API_BASE_URL ?? "/api/v1").replace(/\/+$/, "");
const wsBase = process.env.ITR_WS_BASE_URL ?? "";
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export class ApiDataProvider {
    constructor() {
        this.ws = null;
        this.eventHandlers = new Set();
        this.errorHandlers = new Set();
        this.activeMode = null;
        this.refreshInFlight = null;
        this.auth = {
            login: (email, password) => this.request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
            register: (email, password, name) => this.request("/auth/register", { method: "POST", body: JSON.stringify({ email, password, name }) }),
            refresh: (refreshToken) => this.request("/auth/refresh", { method: "POST", body: JSON.stringify({ refresh_token: refreshToken }) }),
            me: () => this.request("/auth/me"),
        };
        this.tokens = {
            list: () => this.request("/tokens"),
            create: (input) => this.request("/tokens", { method: "POST", body: JSON.stringify(input) }),
            validate: (id) => this.request(`/tokens/${id}/validate`, { method: "POST" }),
            update: (id, input) => this.request(`/tokens/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
            remove: (id) => this.request(`/tokens/${id}`, { method: "DELETE" }),
        };
        this.strategies = {
            list: (mode) => this.request(`/strategies?mode=${mode}`),
            create: (input) => this.request("/strategies", { method: "POST", body: JSON.stringify(input) }),
            update: (id, input) => this.request(`/strategies/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
            remove: (id) => this.request(`/strategies/${id}`, { method: "DELETE" }),
            activate: (id) => this.request(`/strategies/${id}/activate`, { method: "POST" }),
            deactivate: (id) => this.request(`/strategies/${id}/deactivate`, { method: "POST" }),
            clone: (id, targetMode) => this.request(`/strategies/${id}/clone`, { method: "POST", body: JSON.stringify({ target_mode: targetMode }) }),
            versions: (id) => this.request(`/strategies/${id}/versions`),
            rollback: (id, versionId) => this.request(`/strategies/${id}/rollback`, { method: "POST", body: JSON.stringify({ version_id: versionId }) }),
            runtime: (id) => this.request(`/strategies/${id}/runtime`),
            presets: (mode) => this.request(`/strategies/presets?mode=${mode}`),
            listExperiments: (mode) => this.request(`/strategies/experiments?mode=${mode}`),
            createExperiment: (input) => this.request(`/strategies/experiments`, { method: "POST", body: JSON.stringify(input) }),
            updateExperiment: (id, input) => this.request(`/strategies/experiments/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
            experimentKpi: (mode, experimentId) => this.request(`/strategies/experiments/${experimentId}/kpi?mode=${mode}`),
            experimentWinnerSuggestion: (mode, experimentId) => this.request(`/strategies/experiments/${experimentId}/winner-suggestion?mode=${mode}`),
            experimentCommitOutcome: (mode, experimentId, input) => this.request(`/strategies/experiments/${experimentId}/outcome`, {
                method: "POST",
                body: JSON.stringify({ mode, ...input }),
            }),
            experimentDecisionLog: (mode, experimentId) => this.request(`/strategies/experiments/${experimentId}/decision-log?mode=${mode}`),
            listWhatIfExperiments: (mode) => this.request(`/strategies/whatif-experiments?mode=${mode}`),
            createWhatIfExperiment: (input) => this.request(`/strategies/whatif-experiments`, {
                method: "POST",
                body: JSON.stringify(input),
            }),
            updateWhatIfExperiment: (id, input) => this.request(`/strategies/whatif-experiments/${id}`, {
                method: "PATCH",
                body: JSON.stringify(input),
            }),
            deleteWhatIfExperiment: (mode, id) => this.request(`/strategies/whatif-experiments/${id}?mode=${mode}`, {
                method: "DELETE",
            }),
            generateWhatIfReport: (mode, experimentId) => this.request(`/strategies/whatif-experiments/${experimentId}/report?mode=${mode}`, {
                method: "POST",
            }),
        };
        this.strategyGroups = {
            getTree: (mode) => this.request(`/strategy-groups/tree?mode=${mode}`),
            create: (input) => this.request("/strategy-groups", { method: "POST", body: JSON.stringify(input) }),
            update: (id, input) => this.request(`/strategy-groups/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
            remove: (id) => this.request(`/strategy-groups/${id}`, { method: "DELETE" }),
            moveStrategy: (strategyId, input) => this.request(`/strategy-groups/strategies/${strategyId}/move`, {
                method: "POST",
                body: JSON.stringify(input),
            }),
        };
        this.orders = {
            list: (mode, filter) => {
                const query = new URLSearchParams({ mode });
                if (filter?.strategy_id)
                    query.set("strategy_id", filter.strategy_id);
                if (filter?.ticker)
                    query.set("ticker", filter.ticker);
                if (filter?.status)
                    query.set("status", filter.status);
                if (filter?.dateFrom)
                    query.set("dateFrom", filter.dateFrom);
                if (filter?.dateTo)
                    query.set("dateTo", filter.dateTo);
                query.set("page", String(filter?.page ?? 1));
                query.set("page_size", String(filter?.page_size ?? 20));
                return this.request(`/orders?${query.toString()}`);
            },
            create: (input) => this.request("/orders", {
                method: "POST",
                headers: input.client_request_id ? { "Idempotency-Key": input.client_request_id } : undefined,
                body: JSON.stringify(input),
            }),
            cancel: (id) => this.request(`/orders/${id}/cancel`, { method: "POST" }),
            replace: (id, input) => this.request(`/orders/${id}/replace`, { method: "POST", body: JSON.stringify(input) }),
            cancelAll: (mode, filter) => this.request(`/orders/cancel-all`, { method: "POST", body: JSON.stringify({ mode, ...filter }) }),
        };
        this.positions = {
            list: (mode) => this.request(`/positions?mode=${mode}`),
            setSellLock: (input) => this.request(`/positions/sell-lock`, { method: "PUT", body: JSON.stringify(input) }),
            setBlockAllSells: (input) => this.request(`/positions/block-all-sells`, { method: "PUT", body: JSON.stringify(input) }),
            transferStrategy: (input) => this.request(`/positions/transfer-strategy`, { method: "POST", body: JSON.stringify(input) }),
        };
        this.dashboard = {
            get: (mode) => this.request(`/dashboard?mode=${mode}`),
            riskCockpit: (mode) => this.request(`/dashboard/risk-cockpit?mode=${mode}`),
            portfolioComparison: (mode) => this.request(`/dashboard/portfolio-comparison?mode=${mode}`),
            syncPortfolioComparison: (mode) => this.request(`/dashboard/portfolio-comparison/sync?mode=${mode}`, {
                method: "POST",
            }),
            importBrokerTrades: (mode, lookbackDays = 30) => this.request(`/dashboard/broker-trades/import?mode=${mode}&lookback_days=${lookbackDays}`, { method: "POST" }),
            reconcileBrokerTrades: (mode, lookbackDays = 90, importOrphans = true) => this.request(`/dashboard/broker-trades/reconcile?mode=${mode}&lookback_days=${lookbackDays}&import_orphans=${importOrphans}`, { method: "POST" }),
            alignBrokerPositions: (mode, refreshSnapshot = true) => this.request(`/dashboard/broker-trades/align-positions?mode=${mode}&refresh_snapshot=${refreshSnapshot}`, { method: "POST" }),
        };
        this.market = {
            watchlist: (mode) => this.request(`/market/watchlist?mode=${mode}`),
            priceHistory: (mode, ticker, filter) => {
                const query = new URLSearchParams({ mode, ticker: ticker.trim().toUpperCase() });
                if (filter?.dateFrom)
                    query.set("dateFrom", filter.dateFrom);
                if (filter?.dateTo)
                    query.set("dateTo", filter.dateTo);
                if (filter?.limit)
                    query.set("limit", String(filter.limit));
                return this.request(`/market/price-history?${query.toString()}`);
            },
            instrument: (ticker, mode, orderType) => {
                const symbol = ticker.trim().toUpperCase();
                if (!/^[A-Z0-9]{1,12}$/.test(symbol)) {
                    return Promise.resolve({
                        success: false,
                        error: { code: "VALIDATION_ERROR", message: "Некорректный тикер", retryable: false },
                    });
                }
                const query = new URLSearchParams({ mode });
                if (orderType)
                    query.set("order_type", orderType);
                return this.request(`/market/instruments/${encodeURIComponent(symbol)}?${query.toString()}`);
            },
            exchangeSchedule: (mode, exchange = "MOEX") => this.request(`/market/exchange-schedule?mode=${mode}&exchange=${encodeURIComponent(exchange)}`),
        };
        this.trading = {
            readiness: (mode) => this.request(`/trading/readiness?mode=${mode}`),
        };
        this.health = {
            get: () => this.request("/health"),
        };
        this.analytics = {
            summary: (mode) => this.request(`/analytics/summary?mode=${mode}`),
            details: (mode) => this.request(`/analytics/details?mode=${mode}`),
            compare: () => this.request(`/analytics/compare`),
            exportReport: (mode) => this.request(`/analytics/export?mode=${mode}`),
            exportToS3: async (mode) => {
                const created = await this.request(`/exports/analytics?mode=${mode}`, { method: "POST" });
                if (!created.success)
                    return created;
                return this.pollExportJob(created.data.job_id);
            },
            executionQuality: (mode) => this.request(`/analytics/execution-quality?mode=${mode}`),
            pnlReport: (mode, filter) => {
                const query = new URLSearchParams({ mode });
                if (filter?.period)
                    query.set("period", filter.period);
                if (filter?.from)
                    query.set("from", filter.from);
                if (filter?.to)
                    query.set("to", filter.to);
                return this.request(`/analytics/pnl-report?${query.toString()}`);
            },
        };
        this.audit = {
            listTrace: (mode, filter) => {
                const query = new URLSearchParams({ mode });
                if (filter?.strategy_id)
                    query.set("strategy_id", filter.strategy_id);
                if (filter?.ticker)
                    query.set("ticker", filter.ticker);
                if (filter?.dateFrom)
                    query.set("dateFrom", filter.dateFrom);
                if (filter?.dateTo)
                    query.set("dateTo", filter.dateTo);
                if (filter?.causal_tag)
                    query.set("causal_tag", filter.causal_tag);
                return this.request(`/audit/trace?${query.toString()}`);
            },
            exportTraceCsv: (mode, filter) => this.request(`/audit/trace/export`, { method: "POST", body: JSON.stringify({ mode, ...filter }) }),
            listSignalSkips: (mode, filter) => {
                const query = new URLSearchParams({ mode });
                if (filter?.strategy_id)
                    query.set("strategy_id", filter.strategy_id);
                if (filter?.ticker)
                    query.set("ticker", filter.ticker);
                if (filter?.reason_code)
                    query.set("reason_code", filter.reason_code);
                if (filter?.dateFrom)
                    query.set("dateFrom", filter.dateFrom);
                if (filter?.dateTo)
                    query.set("dateTo", filter.dateTo);
                return this.request(`/audit/signal-skips?${query.toString()}`);
            },
        };
        this.preferences = {
            getTrading: () => this.request(`/preferences/trading`),
            updateTrading: (input) => this.request(`/preferences/trading`, { method: "PATCH", body: JSON.stringify(input) }),
        };
        this.risk = {
            get: (mode) => this.request(`/risk?mode=${mode}`),
            update: (mode, input) => this.request(`/risk?mode=${mode}`, { method: "PATCH", body: JSON.stringify(input) }),
            killSwitch: (mode, enabled, scope, strategy_id) => this.request(`/risk/kill-switch`, { method: "POST", body: JSON.stringify({ mode, enabled, scope, strategy_id }) }),
        };
        this.policies = {
            strategy: {
                get: (strategyId) => this.request(`/policies/strategy/${strategyId}`),
                update: (strategyId, input) => this.request(`/policies/strategy/${strategyId}`, { method: "PATCH", body: JSON.stringify(input) }),
            },
            risk: {
                get: (mode) => this.request(`/policies/risk?mode=${mode}`),
                update: (mode, input) => this.request(`/policies/risk?mode=${mode}`, { method: "PATCH", body: JSON.stringify(input) }),
            },
            execution: {
                get: (mode) => this.request(`/policies/execution?mode=${mode}`),
                update: (mode, input) => this.request(`/policies/execution?mode=${mode}`, { method: "PATCH", body: JSON.stringify(input) }),
            },
            runtime: {
                get: (mode) => this.request(`/policy/runtime?mode=${mode}`),
                update: (mode, input) => this.request(`/policy/runtime/${mode}`, { method: "PUT", body: JSON.stringify(input) }),
            },
            portfolio: {
                get: (mode) => this.request(`/policies/portfolio?mode=${mode}`),
                kpi: (mode) => this.request(`/policies/portfolio/kpi?mode=${mode}`),
                update: (mode, input) => this.request(`/policies/portfolio?mode=${mode}`, { method: "PATCH", body: JSON.stringify(input) }),
                usage: (mode) => this.request(`/policies/portfolio/usage?mode=${mode}`),
            },
        };
        this.realtime = {
            subscribe: (mode, topics, options) => {
                this.activeMode = mode;
                this.ensureWs();
                const send = () => this.ws?.send(JSON.stringify({
                    action: "subscribe",
                    mode,
                    topics,
                    reconnect: options?.reconnect ?? false,
                    from_cursor: options?.from_cursor,
                    replay_window_sec: options?.replay_window_sec,
                }));
                if (this.ws?.readyState === WebSocket.OPEN)
                    send();
                else
                    this.ws?.addEventListener("open", send, { once: true });
            },
            unsubscribe: (mode, topics) => {
                if (!this.ws || this.ws.readyState !== WebSocket.OPEN)
                    return;
                this.ws.send(JSON.stringify({ action: "unsubscribe", mode, topics: topics ?? [] }));
            },
            onEvent: (handler) => {
                this.eventHandlers.add(handler);
                return () => this.eventHandlers.delete(handler);
            },
            onError: (handler) => {
                this.errorHandlers.add(handler);
                return () => this.errorHandlers.delete(handler);
            },
        };
        this.admin = {
            listUsers: (params) => {
                const query = new URLSearchParams();
                if (params?.q)
                    query.set("q", params.q);
                if (params?.status)
                    query.set("status", params.status);
                if (params?.page)
                    query.set("page", String(params.page));
                if (params?.page_size)
                    query.set("page_size", String(params.page_size));
                const suffix = query.toString() ? `?${query.toString()}` : "";
                return this.request(`/admin/users${suffix}`);
            },
            setUserActivation: (userId, input) => this.request(`/admin/users/${userId}/activation`, { method: "PATCH", body: JSON.stringify(input) }),
            setUserRoles: (userId, input) => this.request(`/admin/users/${userId}/roles`, { method: "PATCH", body: JSON.stringify(input) }),
            setUserSandbox: (userId, input) => this.request(`/admin/users/${userId}/sandbox`, { method: "PATCH", body: JSON.stringify(input) }),
            getTinvestLimits: (mode = "sandbox") => this.request(`/admin/tinvest-limits?mode=${mode}`),
            triggerTinvestWarmup: () => this.request("/admin/tinvest-limits/warmup", { method: "POST", body: "{}" }),
        };
    }
    parseErrorBody(body, fallbackCode, fallbackMessage, retryable) {
        const err = body;
        return {
            success: false,
            error: {
                code: err.error?.code ?? fallbackCode,
                message: err.error?.message ?? fallbackMessage,
                retryable: err.error?.retryable ?? retryable,
            },
        };
    }
    authHeaders() {
        const session = sessionStorageService.get();
        return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
    }
    isUnauthorized(result) {
        if (result.success)
            return false;
        return result.error.code === "UNAUTHORIZED" || result.error.code === "HTTP_401";
    }
    shouldAttemptRefresh(path) {
        return (!path.startsWith("/auth/login") &&
            !path.startsWith("/auth/register") &&
            !path.startsWith("/auth/refresh"));
    }
    async refreshSession() {
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }
        const stored = sessionStorageService.get();
        if (!stored?.refresh_token) {
            return false;
        }
        this.refreshInFlight = (async () => {
            const result = await this.fetchOnce("/auth/refresh", {
                method: "POST",
                body: JSON.stringify({ refresh_token: stored.refresh_token }),
            });
            if (result.success) {
                sessionStorageService.set(result.data, { notify: true });
                return true;
            }
            sessionStorageService.clear({ notify: true });
            return false;
        })();
        try {
            return await this.refreshInFlight;
        }
        finally {
            this.refreshInFlight = null;
        }
    }
    async fetchOnce(path, init) {
        try {
            const { headers: extraHeaders, ...rest } = init ?? {};
            const response = await fetch(`${apiBase}${path}`, {
                ...rest,
                headers: {
                    "Content-Type": "application/json",
                    ...this.authHeaders(),
                    ...(extraHeaders ?? {}),
                },
            });
            const body = (await response.json().catch(() => ({})));
            const errField = body;
            if (errField.error?.code) {
                return this.parseErrorBody(body, errField.error.code, "Request failed", response.status >= 500);
            }
            if (!response.ok) {
                return this.parseErrorBody(body, `HTTP_${response.status}`, response.statusText ?? "Request failed", response.status >= 500);
            }
            return { success: true, data: body };
        }
        catch (e) {
            return {
                success: false,
                error: {
                    code: "NETWORK_ERROR",
                    message: e instanceof Error ? e.message : "Network error",
                    retryable: true,
                },
            };
        }
    }
    async request(path, init, retried = false) {
        if (this.shouldAttemptRefresh(path) && sessionStorageService.isAccessTokenExpired(sessionStorageService.get())) {
            await this.refreshSession();
        }
        const result = await this.fetchOnce(path, init);
        if (!retried && this.shouldAttemptRefresh(path) && this.isUnauthorized(result)) {
            const refreshed = await this.refreshSession();
            if (refreshed) {
                return this.request(path, init, true);
            }
        }
        return result;
    }
    async pollExportJob(jobId) {
        for (let attempt = 0; attempt < 30; attempt += 1) {
            const result = await this.request(`/exports/jobs/${jobId}`);
            if (!result.success)
                return result;
            const job = result.data;
            if (job.status === "failed") {
                return {
                    success: false,
                    error: {
                        code: job.error_code ?? "EXPORT_FAILED",
                        message: job.error_message ?? "Export failed",
                        retryable: false,
                    },
                };
            }
            if (job.status === "completed" && job.download_url && job.key && job.filename) {
                return {
                    success: true,
                    data: {
                        key: job.key,
                        filename: job.filename,
                        download_url: job.download_url,
                        expires_at: job.expires_at ?? "",
                    },
                };
            }
            await sleep(1000);
        }
        return {
            success: false,
            error: { code: "EXPORT_TIMEOUT", message: "Export job timeout", retryable: true },
        };
    }
    ensureWs() {
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING))
            return;
        const base = wsBase || `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/ws`;
        const auth = this.authHeaders().Authorization?.replace(/^Bearer\s+/i, "");
        const target = auth
            ? `${base}${base.includes("?") ? "&" : "?"}token=${encodeURIComponent(auth)}`
            : base;
        this.ws = new WebSocket(target);
        this.ws.onmessage = (msg) => {
            try {
                const evt = JSON.parse(String(msg.data));
                if (evt.event_type === "ws.error") {
                    this.errorHandlers.forEach((h) => h(evt));
                }
                else {
                    this.eventHandlers.forEach((h) => h(evt));
                }
            }
            catch {
                // ignore malformed frame
            }
        };
        this.ws.onerror = () => {
            const evt = {
                event_id: `ws-err-${Date.now()}`,
                event_type: "ws.error",
                mode: this.activeMode ?? "sandbox",
                user_id: "unknown",
                timestamp: new Date().toISOString(),
                payload: { message: "WebSocket transport error" },
            };
            this.errorHandlers.forEach((h) => h(evt));
        };
    }
}
